import { useState, useEffect, useCallback } from "react";
import { MobileLayout } from "../../components/MobileLayout";
import { useApp } from "../../context/AppContext";
import { supabase } from "../../lib/supabase";
import { appConfig, formatHora, formatDataHora } from "../../data/mock";
import {
  Footprints,
  AlertTriangle,
  Siren,
  Repeat,
  Clock,
  FileText,
  Activity,
  CheckCircle2,
  X,
} from "lucide-react";

const EMPRESA_ID = "ff438dc8-89fb-4d7c-9ea2-7e09162f242a";
const POLL_INTERVAL = 30000;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  pendente:  { label: "Pendente",  bg: "bg-amber-100",   text: "text-amber-700"  },
  aprovada:  { label: "Aprovada",  bg: "bg-emerald-100", text: "text-emerald-700"},
  rejeitada: { label: "Rejeitada", bg: "bg-red-100",     text: "text-red-700"    },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pendente;
  return (
    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${cfg.bg} ${cfg.text}`}>
      {cfg.label}
    </span>
  );
};

// ─── Componente principal ─────────────────────────────────────────────────────

const TIPO_NOME = {
  ronda:      "Rondas",
  ocorrencia: "Ocorrências",
  emergencia: "Alertas",
  passagem:   "Passagens",
};

export default function HistoricoPage() {
  const { user, postos, condominios, getById } = useApp();

  const posto     = getById(postos,      user?.postoId);
  const condominio = getById(condominios, user?.condominioId);

  const [dados, setDados] = useState({
    rondas:      [],
    ocorrencias: [],
    alertas:     [],
    passagens:   [],
    loading:     true,
    ultimaAtualizacao: null,
  });

  const ROLES_SUP = ["supervisor", "admin", "liberty", "esquematiza", "admin_master"];
  const isVigilante = !ROLES_SUP.includes(user?.role);

  const carregar = useCallback(async () => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const amanha = new Date(hoje);
    amanha.setDate(amanha.getDate() + 1);

    const nome = user?.nome;

    let qRondas = supabase
      .from("liberty_ronda_pontos")
      .select("*")
      .eq("empresa_id", EMPRESA_ID)
      .gte("created_at", hoje.toISOString())
      .lt("created_at",  amanha.toISOString())
      .order("created_at", { ascending: false });
    if (isVigilante && nome) qRondas = qRondas.ilike("vigilante_nome", nome);

    let qOc = supabase
      .from("liberty_ocorrencias")
      .select("*")
      .eq("empresa_id", EMPRESA_ID)
      .gte("created_at", hoje.toISOString())
      .lt("created_at",  amanha.toISOString())
      .order("created_at", { ascending: false });
    if (isVigilante && nome) qOc = qOc.ilike("vigilante_nome", nome);

    let qAlertas = supabase
      .from("alertas_panico")
      .select("*")
      .gte("created_at", hoje.toISOString())
      .lt("created_at",  amanha.toISOString())
      .order("created_at", { ascending: false });
    if (isVigilante && nome) qAlertas = qAlertas.ilike("nome_vigilante", nome);

    let qPt = supabase
      .from("liberty_passagem_turno")
      .select("*")
      .eq("empresa_id", EMPRESA_ID)
      .gte("created_at", hoje.toISOString())
      .lt("created_at",  amanha.toISOString())
      .order("created_at", { ascending: false });
    if (isVigilante && nome) qPt = qPt.ilike("de_vigilante_nome", nome);

    const [resRondas, resOc, resAlertas, resPt] = await Promise.all([
      qRondas, qOc, qAlertas, qPt,
    ]);

    if (resRondas.error)  console.error("[Histórico] rondas:",      resRondas.error.message);
    if (resOc.error)      console.error("[Histórico] ocorrências:", resOc.error.message);
    if (resAlertas.error) console.error("[Histórico] alertas:",     resAlertas.error.message);
    if (resPt.error)      console.error("[Histórico] passagens:",   resPt.error.message);

    setDados({
      rondas:      resRondas.data  || [],
      ocorrencias: resOc.data      || [],
      alertas:     resAlertas.data || [],
      passagens:   resPt.data      || [],
      loading:     false,
      ultimaAtualizacao: new Date(),
    });
  }, [isVigilante, user?.nome]); // eslint-disable-line

  useEffect(() => {
    carregar();
    const id = setInterval(carregar, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [carregar]);

  // ─── Monta timeline unificada ───────────────────────────────────────────────

  const eventos = [
    ...dados.rondas.map((r) => ({
      id:        r.id,
      criadoEm:  r.created_at,
      tipo:      "ronda",
      titulo:    r.ponto || "Ponto confirmado",
      subtitulo: r.vigilante_nome || "Vigilante",
      detalhes:  r.observacao || null,
      fotoUrl:   r.foto_url   || null,
      icon:      CheckCircle2,
      cor:       "emerald",
    })),

    ...dados.ocorrencias.map((o) => ({
      id:              o.id,
      criadoEm:        o.created_at,
      tipo:            "ocorrencia",
      titulo:          o.tipo || "Ocorrência",
      subtitulo:       o.vigilante_nome || "Vigilante",
      detalhes:        o.descricao || "Ocorrência registrada no plantão.",
      icon:            AlertTriangle,
      cor:             "amber",
      status:          o.status || "pendente",
      textoEditado:    o.texto_editado   || null,
      textoOriginal:   o.texto_original  || o.descricao || null,
      motivoRejeicao:  o.motivo_rejeicao || null,
    })),

    ...dados.alertas.map((e) => ({
      id:        e.id,
      criadoEm:  e.created_at,
      tipo:      "emergencia",
      titulo:    "Acionamento de emergência",
      subtitulo: e.nome_vigilante || "Vigilante",
      detalhes:  null,
      icon:      Siren,
      cor:       "red",
    })),

    ...dados.passagens.map((p) => ({
      id:        p.id,
      criadoEm:  p.created_at,
      tipo:      "passagem",
      titulo:    `Passagem para ${p.para_vigilante || "próximo vigilante"}`,
      subtitulo: p.de_vigilante_nome || "Vigilante",
      detalhes:  p.resumo || null,
      icon:      Repeat,
      cor:       "purple",
    })),
  ].sort((a, b) => new Date(b.criadoEm) - new Date(a.criadoEm));

  const totalRondas      = dados.rondas.length;
  const totalOcorrencias = dados.ocorrencias.length;
  const totalEmergencias = dados.alertas.length;
  const totalPassagens   = dados.passagens.length;
  const totalEventos     = eventos.length;

  const [eventoSelecionado, setEventoSelecionado] = useState(null);
  const [filtro, setFiltro] = useState(null);
  const eventosFiltrados = filtro ? eventos.filter((e) => e.tipo === filtro) : eventos;

  return (
    <MobileLayout title="Histórico do Plantão" scrollable>
      {/* Cabeçalho */}
      <div className="rounded-2xl bg-gradient-to-br from-[#0d1b3e] to-[#0a1128] border border-white/10 p-5 mb-4">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <p className="tiny-label text-blue-200">Histórico do dia</p>
            <h2 className="font-display font-bold text-lg mt-1">
              Linha do tempo do plantão
            </h2>
            <p className="text-xs text-slate-400 mt-1">
              {posto?.nome || "Posto não informado"} ·{" "}
              <span translate="no">
                {condominio?.nome || appConfig.condominioPadrao}
              </span>
            </p>
          </div>
          <div className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
            <Activity className="w-5 h-5 text-blue-300" />
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2">
          <MiniResumo icon={Footprints}    label="Rondas"  value={dados.loading ? "…" : totalRondas}      active={filtro === "ronda"}      onClick={() => setFiltro(filtro === "ronda"      ? null : "ronda")}      />
          <MiniResumo icon={AlertTriangle} label="Ocorr."  value={dados.loading ? "…" : totalOcorrencias} active={filtro === "ocorrencia"} onClick={() => setFiltro(filtro === "ocorrencia" ? null : "ocorrencia")} />
          <MiniResumo icon={Siren}         label="Alertas" value={dados.loading ? "…" : totalEmergencias} active={filtro === "emergencia"} onClick={() => setFiltro(filtro === "emergencia" ? null : "emergencia")} />
          <MiniResumo icon={Repeat}        label="Pass."   value={dados.loading ? "…" : totalPassagens}   active={filtro === "passagem"}   onClick={() => setFiltro(filtro === "passagem"   ? null : "passagem")}   />
        </div>
      </div>

      {/* Total */}
      <div className="rounded-2xl bg-white/5 border border-white/10 p-4 mb-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="tiny-label text-slate-500">Movimentações</p>
            <p className="text-2xl font-bold mt-1">
              {dados.loading ? "…" : totalEventos}
            </p>
          </div>
          <div className="text-right">
            <p className="tiny-label text-slate-500">Atualizado</p>
            <p className="text-sm text-slate-300 font-mono-data mt-1">
              {dados.ultimaAtualizacao
                ? formatHora(dados.ultimaAtualizacao.toISOString())
                : "—"}
            </p>
          </div>
        </div>
      </div>

      {/* Status do filtro */}
      {!dados.loading && (
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs text-slate-500">
            {filtro
              ? `Mostrando: ${TIPO_NOME[filtro]} (${eventosFiltrados.length} registro${eventosFiltrados.length !== 1 ? "s" : ""})`
              : `Mostrando: todos os registros (${totalEventos})`}
          </p>
          {filtro && (
            <button
              onClick={() => setFiltro(null)}
              className="text-xs text-blue-400 hover:text-blue-300 transition"
            >
              Limpar
            </button>
          )}
        </div>
      )}

      {/* Timeline */}
      <div className="space-y-3">
        {dados.loading && (
          <div className="rounded-2xl bg-[#0a1128] border border-white/10 p-5 text-center">
            <p className="text-sm text-slate-500 italic">Carregando…</p>
          </div>
        )}

        {!dados.loading && eventosFiltrados.length === 0 && (
          <div className="rounded-2xl bg-[#0a1128] border border-white/10 p-5">
            <div className="flex flex-col items-center text-center">
              <FileText className="w-8 h-8 text-slate-500 mb-3" />
              <p className="text-sm font-semibold text-slate-300">
                {filtro ? `Nenhum registro de ${TIPO_NOME[filtro]} no plantão` : "Nenhum registro no plantão"}
              </p>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                As rondas, ocorrências, emergências e passagens de turno
                aparecerão aqui conforme forem registradas.
              </p>
            </div>
          </div>
        )}

        {eventosFiltrados.map((evento) => (
          <HistoricoCard key={evento.id} evento={evento} onClick={() => setEventoSelecionado(evento)} />
        ))}
      </div>

      {eventoSelecionado && (
        <ModalDetalhes evento={eventoSelecionado} onClose={() => setEventoSelecionado(null)} />
      )}
    </MobileLayout>
  );
}

// ─── Cards ────────────────────────────────────────────────────────────────────

const estilos = {
  emerald: { border: "border-emerald-500/30", bg: "bg-emerald-500/10", icon: "text-emerald-300", label: "text-emerald-200" },
  blue:    { border: "border-blue-500/30",    bg: "bg-blue-500/10",    icon: "text-blue-300",    label: "text-blue-200"    },
  amber:   { border: "border-amber-500/30",   bg: "bg-amber-500/10",   icon: "text-amber-300",   label: "text-amber-200"   },
  red:     { border: "border-red-500/30",     bg: "bg-red-500/10",     icon: "text-red-300",     label: "text-red-200"     },
  purple:  { border: "border-purple-500/30",  bg: "bg-purple-500/10",  icon: "text-purple-300",  label: "text-purple-200"  },
};

const HistoricoCard = ({ evento, onClick }) => {
  const Icon = evento.icon;
  const estilo = estilos[evento.cor] || estilos.blue;
  const isOcorrencia = evento.tipo === "ocorrencia";
  const editada = isOcorrencia && evento.textoEditado && evento.textoEditado !== evento.textoOriginal;

  return (
    <div
      className={`rounded-2xl bg-[#0a1128] border ${estilo.border} p-4 cursor-pointer hover:brightness-125 active:scale-[0.99] transition`}
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        <div className={`w-11 h-11 rounded-xl ${estilo.bg} border ${estilo.border} flex items-center justify-center shrink-0`}>
          <Icon className={`w-5 h-5 ${estilo.icon}`} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className={`text-sm font-semibold ${estilo.label}`}>
                  {evento.titulo}
                </p>
                {isOcorrencia && <StatusBadge status={editada ? "aprovada" : evento.status} />}
              </div>
              <p className="text-xs text-slate-500 mt-0.5">{evento.subtitulo}</p>
            </div>
            <span className="text-[11px] font-mono-data text-slate-500 whitespace-nowrap flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatHora(evento.criadoEm)}
            </span>
          </div>

          {/* Texto da ocorrência com diff se editada */}
          {isOcorrencia && editada ? (
            <div className="mt-2 space-y-2">
              <div className="rounded-lg bg-red-500/10 border border-red-500/20 px-3 py-2">
                <p className="text-[10px] text-red-300 font-semibold mb-1">ORIGINAL</p>
                <p className="text-xs text-slate-400 leading-relaxed line-through">
                  {evento.textoOriginal}
                </p>
              </div>
              <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 px-3 py-2">
                <p className="text-[10px] text-emerald-300 font-semibold mb-1">APROVADO (EDITADO)</p>
                <p className="text-xs text-slate-200 leading-relaxed">
                  {evento.textoEditado}
                </p>
              </div>
            </div>
          ) : (
            evento.detalhes && (
              <p className="text-xs text-slate-400 leading-relaxed mt-2 line-clamp-4">
                {evento.detalhes}
              </p>
            )
          )}

          {/* Motivo de rejeição */}
          {isOcorrencia && evento.status === "rejeitada" && evento.motivoRejeicao && (
            <div className="mt-2 rounded-lg bg-red-500/10 border border-red-500/20 px-3 py-2">
              <p className="text-[10px] text-red-300 font-semibold mb-1">MOTIVO DA REJEIÇÃO</p>
              <p className="text-xs text-red-200 leading-relaxed">{evento.motivoRejeicao}</p>
            </div>
          )}

          <p className="text-[11px] text-slate-600 mt-2">
            {formatDataHora(evento.criadoEm)}
          </p>
        </div>
      </div>
    </div>
  );
};

const MiniResumo = ({ icon: Icon, label, value, onClick, active }) => (
  <div
    className={`rounded-xl p-3 cursor-pointer select-none transition ${
      active
        ? "bg-blue-500/20 border border-blue-400/50"
        : "bg-white/5 border border-white/10 hover:bg-white/10"
    }`}
    onClick={onClick}
  >
    <Icon className="w-4 h-4 text-blue-300 mb-2" />
    <p className="text-lg font-bold text-white">{value}</p>
    <p className={`text-[11px] ${active ? "text-blue-200" : "text-slate-500"}`}>{label}</p>
  </div>
);

const TIPO_LABEL = {
  ronda:      "Ronda",
  ocorrencia: "Ocorrência",
  emergencia: "Emergência",
  passagem:   "Passagem de Turno",
};

const ModalDetalhes = ({ evento, onClose }) => {
  const estilo  = estilos[evento.cor] || estilos.blue;
  const Icon    = evento.icon;
  const isOc    = evento.tipo === "ocorrencia";
  const editada = isOc && evento.textoEditado && evento.textoEditado !== evento.textoOriginal;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-3xl bg-[#0d1b3e] border border-white/10 shadow-2xl flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-5 border-b border-white/10`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl ${estilo.bg} border ${estilo.border} flex items-center justify-center`}>
              <Icon className={`w-5 h-5 ${estilo.icon}`} />
            </div>
            <div>
              <p className={`text-sm font-bold ${estilo.label}`}>
                {TIPO_LABEL[evento.tipo] || "Registro"}
              </p>
              <p className="text-xs text-slate-500">{formatDataHora(evento.criadoEm)}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {/* Vigilante */}
          <div>
            <p className="text-[11px] text-slate-500 uppercase tracking-wider mb-1">Vigilante</p>
            <p className="text-sm font-semibold text-white">{evento.subtitulo}</p>
          </div>

          {/* Ronda — seção completa */}
          {evento.tipo === "ronda" && (
            <>
              <div>
                <p className="text-[11px] text-slate-500 uppercase tracking-wider mb-1">Ponto confirmado</p>
                <p className="text-sm text-slate-200">{evento.titulo}</p>
              </div>

              <div>
                <p className="text-[11px] text-slate-500 uppercase tracking-wider mb-2">Foto</p>
                {evento.fotoUrl ? (
                  <img
                    src={evento.fotoUrl}
                    alt="Foto do ponto"
                    style={{ width: "100%", borderRadius: 8, marginTop: 8, maxHeight: 220, objectFit: "cover" }}
                  />
                ) : (
                  <p className="text-xs text-slate-500 italic">Nenhuma foto registrada</p>
                )}
              </div>

              {evento.detalhes && (
                <div>
                  <p className="text-[11px] text-slate-500 uppercase tracking-wider mb-1">Observação</p>
                  <div className="rounded-xl bg-white/5 border border-white/10 px-3 py-3">
                    <p className="text-sm text-slate-300 leading-relaxed">{evento.detalhes}</p>
                  </div>
                </div>
              )}

              <div>
                <p className="text-[11px] text-slate-500 uppercase tracking-wider mb-1">Horário exato</p>
                <p className="text-sm font-mono-data text-slate-200">
                  {new Date(evento.criadoEm).toLocaleDateString("pt-BR", {
                    day: "2-digit", month: "2-digit", year: "numeric",
                  })}{" "}às{" "}
                  {new Date(evento.criadoEm).toLocaleTimeString("pt-BR", {
                    hour: "2-digit", minute: "2-digit", second: "2-digit",
                  })}
                </p>
              </div>
            </>
          )}

          {/* Passagem — título */}
          {evento.tipo === "passagem" && (
            <div>
              <p className="text-[11px] text-slate-500 uppercase tracking-wider mb-1">Passagem</p>
              <p className="text-sm text-slate-200">{evento.titulo}</p>
            </div>
          )}

          {/* Status (ocorrência) */}
          {isOc && (
            <div>
              <p className="text-[11px] text-slate-500 uppercase tracking-wider mb-1">Status</p>
              <StatusBadge status={editada ? "aprovada" : evento.status} />
            </div>
          )}

          {/* Foto (não-ronda) */}
          {evento.fotoUrl && evento.tipo !== "ronda" && (
            <div>
              <p className="text-[11px] text-slate-500 uppercase tracking-wider mb-2">Foto</p>
              <img
                src={evento.fotoUrl}
                alt="Foto do registro"
                className="w-full rounded-xl border border-white/10 object-cover max-h-48"
              />
            </div>
          )}

          {/* Descrição / observação normal (não-ronda, não-ocorrência) */}
          {!isOc && evento.tipo !== "ronda" && evento.detalhes && (
            <div>
              <p className="text-[11px] text-slate-500 uppercase tracking-wider mb-1">Observação</p>
              <p className="text-sm text-slate-300 leading-relaxed">{evento.detalhes}</p>
            </div>
          )}

          {/* Ocorrência com diff */}
          {isOc && editada && (
            <div className="space-y-2">
              <p className="text-[11px] text-slate-500 uppercase tracking-wider">Descrição</p>
              <div className="rounded-xl bg-red-500/10 border border-red-500/20 px-3 py-3">
                <p className="text-[10px] text-red-300 font-semibold mb-1">ORIGINAL</p>
                <p className="text-xs text-slate-400 leading-relaxed line-through">{evento.textoOriginal}</p>
              </div>
              <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/20 px-3 py-3">
                <p className="text-[10px] text-emerald-300 font-semibold mb-1">APROVADO (EDITADO)</p>
                <p className="text-xs text-slate-200 leading-relaxed">{evento.textoEditado}</p>
              </div>
            </div>
          )}

          {/* Ocorrência sem edição */}
          {isOc && !editada && evento.detalhes && (
            <div>
              <p className="text-[11px] text-slate-500 uppercase tracking-wider mb-1">Descrição</p>
              <p className="text-sm text-slate-300 leading-relaxed">{evento.detalhes}</p>
            </div>
          )}

          {/* Motivo de rejeição */}
          {isOc && evento.status === "rejeitada" && evento.motivoRejeicao && (
            <div className="rounded-xl bg-red-500/10 border border-red-500/20 px-3 py-3">
              <p className="text-[10px] text-red-300 font-semibold mb-1">MOTIVO DA REJEIÇÃO</p>
              <p className="text-xs text-red-200 leading-relaxed">{evento.motivoRejeicao}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
